import { z } from "zod";
import {
  HEALTH_NOTE_MAX,
  HEALTH_QUESTION_TEXT_MAX,
  HEALTH_QUESTIONS_MAX,
  WAIVER_TEXT_MAX,
} from "../health-form.ts";
import { isoDateTimeSchema, uuidSchema } from "./common.ts";

/**
 * Health notes and the waiver at join (C2, migration 0077). See `health-form.ts` for the rules.
 * Answer text travels only in `participantHealthSchema.details`, which the server fills for the owner,
 * the admins, and staff when the owner allows it.
 */

export const healthQuestionSchema = z.object({
  id: z.string().regex(/^[a-z0-9_]{1,24}$/),
  text: z.string().trim().min(3).max(HEALTH_QUESTION_TEXT_MAX),
});

export const waiverSchema = z.object({
  /** 1 for the first text; a change of the text makes the next number. */
  version: z.number().int().min(1),
  text: z.string(),
});
export type Waiver = z.infer<typeof waiverSchema>;

/** `GET /v1/owner/businesses/:id/health-form`: the form as the owner sets it up. */
export const healthFormSchema = z.object({
  enabled: z.boolean(),
  /** Staff ("Instructors can read health notes") read the details too. Off by default. */
  staffCanRead: z.boolean(),
  questions: z.array(healthQuestionSchema),
  /** The current terms, or null when the business has none. */
  waiver: waiverSchema.nullable(),
  /** People who answered at this business. A count only. */
  answeredCount: z.number().int().min(0),
});
export type HealthForm = z.infer<typeof healthFormSchema>;

/**
 * `PUT /v1/owner/businesses/:id/health-form`. Any subset. A new `waiverText` (other than the current
 * text) makes a new version; an empty text removes the terms. People who answered before keep their
 * answers and the version they accepted (V1 does not ask them again).
 */
export const updateHealthFormRequestSchema = z
  .object({
    enabled: z.boolean(),
    staffCanRead: z.boolean(),
    questions: z.array(healthQuestionSchema).min(1).max(HEALTH_QUESTIONS_MAX),
    waiverText: z.string().trim().max(WAIVER_TEXT_MAX),
  })
  .partial();
export type UpdateHealthFormRequest = z.infer<typeof updateHealthFormRequestSchema>;

/** `GET /v1/me/businesses/:id/health-form`: what the member answers, and for whom. */
export const memberHealthFormSchema = z.object({
  enabled: z.boolean(),
  /** The owner lets staff read the answers, so the step can say who reads them. */
  staffCanRead: z.boolean(),
  questions: z.array(healthQuestionSchema),
  waiver: waiverSchema.nullable(),
  /**
   * The people of this member here who did not answer yet: the client (where clients book for
   * themselves) or each child (a family business). Empty while the form is off.
   */
  pending: z.array(z.object({ participantId: uuidSchema, name: z.string() })),
});
export type MemberHealthForm = z.infer<typeof memberHealthFormSchema>;

/** `POST /v1/me/businesses/:id/health-form`: the answers of one person. */
export const submitHealthAnswersRequestSchema = z.object({
  participantId: uuidSchema,
  answers: z
    .array(
      z.object({
        questionId: z.string().max(24),
        yes: z.boolean(),
        note: z.string().trim().max(HEALTH_NOTE_MAX).nullable().optional(),
      }),
    )
    .max(HEALTH_QUESTIONS_MAX),
  /** The version of the terms the person accepted. Required when the business has terms. */
  waiverVersion: z.number().int().min(1).nullable(),
});
export type SubmitHealthAnswersRequest = z.infer<typeof submitHealthAnswersRequestSchema>;

export const submitHealthAnswersResponseSchema = z.object({
  hasFlag: z.boolean(),
  /** People of this member here who still did not answer. */
  pending: memberHealthFormSchema.shape.pending,
});

/**
 * `GET /v1/business/businesses/:id/participants/:participantId/health`. Every person on the team
 * gets the flag; `details` is null unless the reader may read them (the owner, an admin, or staff
 * when the owner allows it).
 */
export const participantHealthSchema = z.object({
  participantId: uuidSchema,
  /** The person answered the form at this business. */
  answered: z.boolean(),
  hasFlag: z.boolean(),
  /** The reader may read the answers. False: `details` is null. */
  canRead: z.boolean(),
  details: z
    .object({
      answeredAt: isoDateTimeSchema,
      answers: z.array(
        z.object({ questionId: z.string(), question: z.string(), yes: z.boolean(), note: z.string().nullable() }),
      ),
      /** The terms this person accepted, or null. */
      waiver: z.object({ version: z.number().int(), acceptedAt: isoDateTimeSchema }).nullable(),
    })
    .nullable(),
});
export type ParticipantHealth = z.infer<typeof participantHealthSchema>;
